<template>
	<div ref="dropdownRef" class="relative w-fit">

		<!-- Add .stop modifier to prevent bubbling to document -->
		<button
			type="button"
			class="btn btn-sm btn-ghost flex items-center justify-center gap-2 mb-2"
			@click="showFilterOptions = !showFilterOptions"
		>
			<span class="flex items-center gap-1">
				<NoSymbolIcon v-if="selectedSet.options.length === 0" class="w-6 h-6 shrink-0" />
				<img v-for="option in selectedSet.options" :key="option" :src="feedbackIcons[option]" class="w-6 h-6 shrink-0" alt="" />
			</span>
			<ChevronDownIcon class="w-3 h-3" />
		</button>

		<p v-show="selectedSet.options.length" class="p-2 text-xs text-base-content-light italic">
			{{ $t('Feedback is only asked when the pupil has spent at least 30 seconds on the activity') }}
		</p>

		<!-- Filter options dropdown -->
		<div v-if="showFilterOptions" class="absolute z-10 top-full left-0 w-max max-w-[min(20rem,calc(100vw-4rem))]">
			<div class="mb-4 bg-base p-2 shadow-lg rounded-lg flex flex-col gap-2" style="animation: button-pop 0.25s ease-out;">
				<button
					type="button"
					v-for="set in optionSets"
					:key="set.key"
					@click="selectOptions(set)"
					class="btn-effect flex items-center gap-3 px-3 py-2 rounded-lg text-left"
					:class="set.key === selectedSet.key ? 'bg-indigo-200' : 'hover:bg-base-100'"
				>
					<span class="flex items-center gap-1 min-w-0">
						<NoSymbolIcon v-if="set.options.length === 0" class="w-6 h-6 shrink-0" />
						<img v-for="option in set.options" :key="option" :src="feedbackIcons[option]" class="w-6 h-6 shrink-0" alt="" />
						<span class="min-w-0 break-words ml-2 text-xs">{{ set.label ?? '' }}</span>
					</span>
				</button>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { mapStores } from "pinia";
import {
	useActivityBoardStore,
	PUPIL_FEEDBACK_OPTION_SETS,
	type PupilFeedbackOption,
} from '@/stores/ActivityBoard.store';
import { ChevronDownIcon, NoSymbolIcon } from "@heroicons/vue/24/outline";

const FEEDBACK_ICONS: Record<PupilFeedbackOption, string> = {
	1: '/images/icons/Ph-happy.svg',
	0: '/images/icons/Ph-neutral.svg',
	'-1': '/images/icons/Ph-sad.svg',
};

export default {
	data() {
		return {
			showFilterOptions: false,
			feedbackIcons: FEEDBACK_ICONS,
		}
	},

	components: {
		ChevronDownIcon,
		NoSymbolIcon,
	},

	computed: {
		...mapStores(useActivityBoardStore),

		optionSets() {
			return [
				{ key: 'none', options: PUPIL_FEEDBACK_OPTION_SETS.none, label: this.$t('Ask no feedback after activity') },
				{ key: 'all', options: PUPIL_FEEDBACK_OPTION_SETS.all },
				{ key: 'positiveNegative', options: PUPIL_FEEDBACK_OPTION_SETS.positiveNegative },
			];
		},

		selectedSet() {
			const current: PupilFeedbackOption[] = this.activityBoardStore.pupilFeedbackOptions;
			return this.optionSets.find(set =>
				set.options.length === current.length && set.options.every(option => current.includes(option))
			) ?? this.optionSets[0];
		},
	},

	mounted() {
		document.addEventListener('pointerdown', this.handleClickOutside, true);
	},

	beforeUnmount() {
		document.removeEventListener('pointerdown', this.handleClickOutside, true);
	},

	methods: {
		handleClickOutside(event: PointerEvent) {
			const el = this.$refs.dropdownRef as HTMLElement | HTMLElement[] | undefined;

			if (!this.showFilterOptions || !el || Array.isArray(el)) return;

			if (!event.composedPath().includes(el)) {
				this.showFilterOptions = false;
			}
		},

		selectOptions(set: { key: string, options: PupilFeedbackOption[] }) {
			this.activityBoardStore.setPupilFeedbackOptions(set.options);
			this.showFilterOptions = false;

			const gtag = (this as any).$gtag;
			if (gtag) {
				gtag.event('select-pupil-feedback-options', {
					'event_category': 'activity-board',
					'event_label': 'configuration',
					'value': set.key,
				});
			}
		},
	}
}
</script>
